import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import KpiCard from "../components/KpiCard";
import { fmtINR, fmtDate } from "../lib/format";
import { ArrowDownCircle, ArrowUpCircle, Search } from "lucide-react";
import ExportMenu from "../components/ExportMenu";

const RECON_COLOR = { "Matched": "#4af6c3", "In Transit": "#fb8b1e", "Book Only": "#0068ff", "Bank Only": "#ff433d" };
const TYPE_COLOR = { "Credit": "#4af6c3", "Debit": "#ff433d" };

export default function Banking() {
    const [data, setData] = useState(null);
    const [txns, setTxns] = useState(null);
    const [tab, setTab] = useState("accounts");
    const [selectedAcc, setSelectedAcc] = useState("");
    const [txnType, setTxnType] = useState("");
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/banking/accounts").then((r) => setData(r.data));
    }, []);

    useEffect(() => {
        const params = new URLSearchParams();
        if (selectedAcc) params.set("account_id", selectedAcc);
        if (txnType) params.set("type", txnType);
        const qs = params.toString();
        api.get(`/banking/transactions${qs ? "?" + qs : ""}`).then((r) => setTxns(r.data));
    }, [selectedAcc, txnType]);

    if (!data) return <div className="font-mono-fin text-sm text-[var(--text-secondary)]">// LOADING BANKING...</div>;

    const filteredTxns = (txns?.transactions || []).filter((t) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return t.party.toLowerCase().includes(s) || t.narration.toLowerCase().includes(s) || t.ref.toLowerCase().includes(s);
    });

    return (
        <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// BANKING & TREASURY</div>
                    <h1 className="font-display font-black text-3xl">Multi-Bank Position</h1>
                    <div className="font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider mt-1">
                        5 accounts · <Link to="/reconciliation" className="text-[var(--brand-amber)] hover:underline">Reconciliation →</Link> · <Link to="/cash" className="text-[var(--brand-amber)] hover:underline">Cash Book →</Link>
                    </div>
                </div>
                <ExportMenu kind="banking" testId="export-banking" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <KpiCard testId="bank-positive" label="Cash & Bank Balance" value={data.summary.total_positive} sub="Positive accounts" accent />
                <KpiCard testId="bank-utilized" label="Facility Utilized" value={data.summary.total_utilized} sub="CC + OD" />
                <KpiCard testId="bank-limit" label="Total Facility Limit" value={data.summary.total_limit} sub="Sanctioned" />
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 border-b border-panel">
                {[
                    { key: "accounts", label: "Accounts" },
                    { key: "transactions", label: "Transactions" },
                ].map((t) => (
                    <button
                        key={t.key}
                        data-testid={`bank-tab-${t.key}`}
                        onClick={() => setTab(t.key)}
                        className={`px-4 py-2 text-xs font-mono-fin uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
                            tab === t.key ? "border-[var(--brand-amber)] text-[var(--text-primary)]" : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "accounts" && (
                <Panel testId="bank-accounts" title="Bank Accounts" subtitle="Live positions">
                    <div className="overflow-x-auto">
                        <table className="w-full font-mono-fin text-xs">
                            <thead className="border-b border-panel">
                                <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                    <th className="text-left py-2">Bank / Branch</th>
                                    <th className="text-left">A/C No</th>
                                    <th className="text-left">Type</th>
                                    <th className="text-right">Balance</th>
                                    <th className="text-right">Limit</th>
                                    <th className="text-right">Utilization</th>
                                    <th className="text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.accounts.map((a) => {
                                    const util = a.limit ? Math.abs(a.balance) / a.limit * 100 : null;
                                    return (
                                        <tr key={a.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                            <td className="py-2 text-[var(--text-primary)]">{a.bank} · <span className="text-[var(--text-secondary)]">{a.branch}</span></td>
                                            <td className="text-[var(--text-secondary)]">{a.acc_no}</td>
                                            <td><span className="text-[10px] px-1.5 py-0.5 border border-panel uppercase tracking-wider">{a.type}</span></td>
                                            <td className={`text-right ${a.balance >= 0 ? "text-pos" : "text-neg"}`}>{fmtINR(a.balance)}</td>
                                            <td className="text-right text-[var(--text-primary)]">{a.limit ? fmtINR(a.limit) : "—"}</td>
                                            <td className="text-right">
                                                {util != null ? (
                                                    <div className="inline-flex items-center gap-2">
                                                        <div className="w-16 h-1 bg-[var(--bg-base)] border border-panel">
                                                            <div className="h-full" style={{ width: `${util}%`, background: util > 80 ? "#ff433d" : util > 60 ? "#fb8b1e" : "#4af6c3" }} />
                                                        </div>
                                                        <span className={util > 80 ? "text-neg" : util > 60 ? "text-amber" : "text-pos"}>{util.toFixed(0)}%</span>
                                                    </div>
                                                ) : "—"}
                                            </td>
                                            <td className="text-right">
                                                <button
                                                    data-testid={`view-txns-${a.id}`}
                                                    onClick={() => { setSelectedAcc(a.id); setTab("transactions"); }}
                                                    className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--brand-amber)] hover:underline"
                                                >
                                                    View Ledger →
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Panel>
            )}

            {tab === "transactions" && (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard testId="txn-credit" label="Total Credits" value={txns?.summary.total_credit || 0} sub="Inflow" />
                        <KpiCard testId="txn-debit" label="Total Debits" value={txns?.summary.total_debit || 0} sub="Outflow" />
                        <KpiCard testId="txn-net" label="Net Movement" value={txns?.summary.net || 0} sub={`${txns?.summary.count || 0} txns`} accent />
                        <KpiCard testId="txn-unrecon" label="Unreconciled" value={txns?.summary.unreconciled || 0} format="custom" sub="Needs attention" />
                    </div>

                    <Panel testId="bank-txns" title="Bank Transaction Ledger" right={
                        <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative">
                                <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
                                <input
                                    data-testid="txn-search"
                                    value={q}
                                    onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search party / ref"
                                    className="bg-[var(--bg-base)] border border-panel pl-7 pr-2 py-1 text-xs font-mono-fin w-44"
                                />
                            </div>
                            <select
                                data-testid="txn-account-filter"
                                value={selectedAcc}
                                onChange={(e) => setSelectedAcc(e.target.value)}
                                className="bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs font-mono-fin uppercase tracking-wider"
                            >
                                <option value="">All Accounts</option>
                                {data.accounts.map((a) => <option key={a.id} value={a.id}>{a.bank} · {a.acc_no}</option>)}
                            </select>
                            <select
                                data-testid="txn-type-filter"
                                value={txnType}
                                onChange={(e) => setTxnType(e.target.value)}
                                className="bg-[var(--bg-base)] border border-panel px-2 py-1 text-xs font-mono-fin uppercase tracking-wider"
                            >
                                <option value="">All</option>
                                <option value="Credit">Credit</option>
                                <option value="Debit">Debit</option>
                            </select>
                        </div>
                    }>
                        <div className="overflow-x-auto">
                            <table className="w-full font-mono-fin text-xs">
                                <thead className="border-b border-panel">
                                    <tr className="text-[10px] uppercase tracking-wider text-[var(--text-secondary)]">
                                        <th className="text-left py-2">Date</th>
                                        <th className="text-left">Bank</th>
                                        <th className="text-left">Mode</th>
                                        <th className="text-left">Party / Narration</th>
                                        <th className="text-left">Ref</th>
                                        <th className="text-right">Amount</th>
                                        <th className="text-left">Recon</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTxns.map((t) => (
                                        <tr key={t.id} className="border-b border-[var(--border)]/50 hover:bg-[var(--bg-panel-hover)]">
                                            <td className="py-2 text-[var(--text-secondary)]">{fmtDate(t.date)}</td>
                                            <td className="text-[var(--text-primary)]">{t.bank}<span className="text-[var(--text-muted)] block text-[10px]">{t.acc_no}</span></td>
                                            <td className="text-[var(--text-secondary)] text-[10px] uppercase tracking-wider">{t.mode}</td>
                                            <td className="text-[var(--text-primary)]">
                                                <div className="flex items-center gap-2">
                                                    {t.type === "Credit" ? <ArrowDownCircle size={12} className="text-pos" /> : <ArrowUpCircle size={12} className="text-neg" />}
                                                    <div>
                                                        <div>{t.party}</div>
                                                        <div className="text-[10px] text-[var(--text-muted)]">{t.narration}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="text-[var(--text-secondary)] text-[10px]">{t.ref}</td>
                                            <td className={`text-right font-medium ${TYPE_COLOR[t.type] === "#4af6c3" ? "text-pos" : "text-neg"}`}>
                                                {t.type === "Credit" ? "+" : "−"}{fmtINR(t.amount)}
                                            </td>
                                            <td>
                                                <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 border" style={{ borderColor: RECON_COLOR[t.recon_status], color: RECON_COLOR[t.recon_status] }}>{t.recon_status}</span>
                                            </td>
                                        </tr>
                                    ))}
                                    {filteredTxns.length === 0 && (
                                        <tr><td colSpan={7} className="text-center py-8 text-[var(--text-secondary)] font-mono-fin text-xs">No transactions match filters.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Panel>
                </>
            )}
        </div>
    );
}
